import { Link, useParams } from "react-router-dom";
import { findArticle, ARTICLES } from "@/data/articles";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Sparkles } from "lucide-react";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);

  useSeo({
    title: article ? `${article.title} | ChantiPlanning` : "Article introuvable",
    description: article?.description || "",
    canonicalPath: `/blog/${slug}`,
  });

  if (!article) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-[#0F2137]">Article introuvable</h1>
        <Link to="/blog">
          <Button className="mt-4">Retour au blog</Button>
        </Link>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid="blog-article">
      <Link
        to="/blog"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2137] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Blog
      </Link>

      <div className="text-5xl mb-4">{article.heroEmoji}</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F2137] leading-tight tracking-tight">
        {article.title}
      </h1>
      <div className="mt-4 flex items-center gap-4 text-xs font-mono-tech uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {article.readingMin} min
        </span>
        <span>·</span>
        <time>{new Date(article.publishedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</time>
      </div>

      <div className="mt-8 space-y-4">
        {article.sections.map((s, i) => {
          if (s.type === "h2")
            return (
              <h2 key={i} className="font-display font-bold text-2xl text-[#0F2137] pt-6">
                {s.content}
              </h2>
            );
          if (s.type === "p")
            return (
              <p key={i} className="text-slate-700 leading-relaxed">
                {s.content}
              </p>
            );
          if (s.type === "ul")
            return (
              <ul key={i} className="list-disc pl-6 space-y-1.5 text-slate-700">
                {s.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            );
          return null;
        })}
      </div>

      {/* CTA */}
      <div className="mt-14 rounded-2xl bg-[#0F2137] text-white p-8 ring-2 ring-[#F59E0B]">
        <div className="font-display text-2xl font-bold">
          Générez votre planning en 60 secondes
        </div>
        <p className="text-slate-300 mt-2 text-sm">
          Testez gratuitement la génération automatique à partir de vos devis, sans inscription.
        </p>
        <Link to="/nouveau">
          <Button className="mt-5 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold h-11">
            <Sparkles className="w-4 h-4 mr-1.5" /> Essayer gratuitement
          </Button>
        </Link>
      </div>

      {related.length > 0 && (
        <div className="mt-16 border-t border-slate-200 pt-10">
          <div className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-4">
            À lire aussi
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                className="rounded-lg border border-slate-200 bg-white p-5 hover:border-[#F59E0B] transition-colors"
              >
                <div className="text-2xl mb-2">{r.heroEmoji}</div>
                <div className="font-display font-semibold text-[#0F2137]">{r.title}</div>
                <div className="text-xs text-slate-500 mt-2 line-clamp-2">{r.description}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
